import type { App, Project } from '@hangar/shared';
import type { Profile } from '@/api/schemas';
import { useCan, useFeature } from '@/lib/permissions';
import { useRunBuild } from '@/shell/RunBuildContext';
import { startsFromConsole } from '@/features/builds/buildHelpers';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';

const RUN_UNAVAILABLE = 'Run build from the console is not available on this Hangar yet: start builds with hangar build';

/** Run only where Hangar starts the build itself (startsFromConsole): a CI-only profile builds with `hangar build`. */
export function RunProfileButton({ profile, app, project, compact = false }: { profile: Profile; app: App; project: Project; compact?: boolean }) {
  const { openRunBuild } = useRunBuild();
  const canBuild = useCan('builds:write', project.id);
  const runBuild = useFeature('runBuild');
  const runner = useFeature('runner');
  if (!startsFromConsole(profile, runner)) return null;
  return (
    <GatedButton
      size="sm"
      variant="outline"
      data-testid="profile-run"
      can={canBuild}
      blockedReason={runBuild === false ? RUN_UNAVAILABLE : undefined}
      aria-label={compact ? `Run ${profile.name}` : undefined}
      onClick={() => openRunBuild({ projectSlug: project.slug, appId: app.id, profileId: profile.id })}
    >
      <Icon name="bolt" size={16} />
      <span className={compact ? '@max-[36rem]:sr-only' : undefined}>Run</span>
    </GatedButton>
  );
}
